import { useState } from "react";
import { Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/roles";
import { Input } from "@/components/ui/input";
import { CopyLink } from "@/components/app/CopyLink";

const selectCls = "mt-2 h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm text-oath-ink";

export const InviteForm = ({ roles, orgs, endpoint, onCreated, testid = "invite" }) => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(roles[0]);
  const [orgId, setOrgId] = useState("");
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState(null);
  const isStaff = ["super_admin", "analyst", "qa_reviewer", "field_agent"].includes(role);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = orgs ? (isStaff ? { email, platform_role: role } : { email, org_role: role, org_id: orgId }) : { email, org_role: role };
      const { data } = await api.post(endpoint, body);
      setLink(data);
      setEmail("");
      onCreated?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-2xl border border-gray-200 bg-white p-5" data-testid={`${testid}-form`}>
      <div className="flex items-center gap-2 font-display font-bold text-oath-ink"><UserPlus className="h-4 w-4 text-oath-accent" /> Invite someone</div>
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <div>
          <label className="font-mono-label text-[9px] font-semibold text-oath-muted">EMAIL</label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2" data-testid={`${testid}-email-input`} />
        </div>
        <div>
          <label className="font-mono-label text-[9px] font-semibold text-oath-muted">ROLE</label>
          <select value={role} onChange={(e) => setRole(e.target.value)} className={selectCls} data-testid={`${testid}-role-select`}>
            {roles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
        </div>
        {orgs && !isStaff && (
          <div>
            <label className="font-mono-label text-[9px] font-semibold text-oath-muted">CLIENT COMPANY</label>
            <select required value={orgId} onChange={(e) => setOrgId(e.target.value)} className={selectCls} data-testid={`${testid}-org-select`}>
              <option value="">Select…</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
        )}
      </div>
      <button type="submit" disabled={loading} data-testid={`${testid}-submit-button`}
        className="mt-4 inline-flex items-center gap-2 rounded-full bg-oath-ink px-5 py-2.5 text-xs font-semibold text-white hover:bg-black disabled:opacity-60">
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Create invite link
      </button>
      {link && (
        <div className="mt-4" data-testid={`${testid}-result`}>
          <p className="mb-2 text-xs text-oath-secondary">Send this link to {link.email}. It works once and expires in 7 days.</p>
          <CopyLink path={link.invite_path} testid={`${testid}-link`} />
        </div>
      )}
    </form>
  );
};

export default InviteForm;
